import { z } from 'zod';

/**
 * Every action the audit log accepts. Mirrors `public.audit_actions`: `audit-actions.fixture.json`
 * is dumped from the seeded local database by `bun run db:types` and a test asserts the two agree. Adding one:
 * here, plus an insert in a new migration.
 */
export const AUDIT_ACTIONS = [
  'org.created',
  'org.updated',
  'org.deleted',
  'workspace.created',
  'workspace.renamed',
  'workspace.archived',
  'workspace.unarchived',
  'member.role_changed',
  'member.removed',
  'member.left',
  'grant.set',
  'grant.revoked',
  'project.moved',
  'project.started',
  'project.draft_discarded',
  'project.draft_purged',
  'project.ordered',
  'script.uploaded',
  'script.removed',
  'invitation.created',
  'invitation.resent',
  'invitation.revoked',
  'invitation.accepted',
  'profile.updated',
  // staff path (actor_kind `staff`; `system` for the bootstrap)
  'staff.role_granted',
  'staff.role_revoked',
  'staff.org_created',
  'staff.org_updated',
  'staff.owner_invited',
  'staff.project.viewed',
  'project.version_uploaded',
  'project.version_deleted',
  'staff.script.downloaded',
  'staff.script.viewed',
  'staff.script_entities.confirmed',
  'staff.run_log.viewed',
  // billing
  'billing_profile.updated',
  'order.created',
  'order.updated',
  'order.submitted',
  'order.placed',
  'order.expired',
  'order.reopened',
  'order.cancelled',
  'order.refunded',
  // stored cards
  'payment_method.added',
  'payment_method.removed',
  'payment_method.default_changed',
  // pricing
  'staff.price_list_created',
  'staff.price_list_version_published',
  'staff.org_pricing_set',
  // payment on account
  'staff.payment_mode_set',
  'staff.credit_limit_set',
  'staff.billing_visibility_set',
  // ledger
  'staff.invoice_recorded',
  'staff.invoice_paid',
  'staff.invoice_voided',
  'staff.credit_note_issued',
  'staff.adjustment_posted',
  'staff.entry_reversed',
] as const;
export const auditActionSchema = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof auditActionSchema>;

/** `system` is a change with no signed-in caller (the signup trigger); `staff` a change made on the staff path. */
export const auditActorKindSchema = z.enum(['customer', 'staff', 'system']);
export type AuditActorKind = z.infer<typeof auditActorKindSchema>;

/** A row of `public.audit_log`. Append-only; staff read it (`staff.audit.view`), customers never do. */
export const auditEntrySchema = z.object({
  id: z.number().int(),
  at: z.iso.datetime({ offset: true }),
  actor_id: z.uuid().nullable(),
  actor_kind: auditActorKindSchema,
  action: auditActionSchema,
  organization_id: z.uuid().nullable(),
  target_type: z.string(),
  target_id: z.string(),
  details: z.record(z.string(), z.unknown()),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

/** A row of `staff_list_audit`: the entry plus the actor's email and the org's name, for display. */
export const auditListEntrySchema = auditEntrySchema.extend({
  actor_email: z.string().nullable(),
  organization_name: z.string().nullable(),
});
export type AuditListEntry = z.infer<typeof auditListEntrySchema>;
